import type { IPublicClientApplication } from '@azure/msal-browser'
import CircularProgress from '@mui/material/CircularProgress'
import { Component, type ReactNode } from 'react'
import { useIntl } from 'react-intl'

import { forgetHopTabSignIn } from '@/shared/auth/msal-instance'
import {
  abandonPendingHop,
  clearSignOutReturn,
  holdHopSignIn,
  hopFailureAddress,
  hopNavigation,
  hopSignOutPath,
  isHopPagePath,
  isKnownSite,
  isRoutePath,
  readHopId,
  readPendingHopReturn,
  readSignOutReturn,
  readStoredSignOutReturn,
} from '@/shared/auth/sign-in-hop'
import { log, LogLevel } from '@/shared/logging'

/**
 * What the sign-in site shows while another site's sign-in or sign-out passes through it (SM-04):
 * nothing of its own, no header, navigation or footer, only a neutral spinner on a blank page.
 */
export function PassThrough() {
  const intl = useIntl()

  return (
    <div className="flex min-h-screen items-center justify-center bg-background">
      <CircularProgress aria-label={intl.formatMessage({ id: 'auth.callback.completing' })} />
    </div>
  )
}

/** What a failed pass-through page knew of where to go back to, read before any of it is forgotten. */
export interface PassThroughState {
  /** The site the API's record of a pending hop names, while the API still keeps that hop. */
  readonly pendingReturn: string | null
  readonly signOutReturn: string | null
  /** The address's fragment, as the hop or sign-out page was sent with it. */
  readonly hash: string
  /** The failed page's path. */
  readonly pathname: string
}

/** Reads {@link PassThroughState} from this tab. */
export function readPassThroughState(): PassThroughState {
  return {
    pendingReturn: readPendingHopReturn(),
    signOutReturn: readStoredSignOutReturn(),
    hash: window.location.hash,
    pathname: window.location.pathname,
  }
}

/**
 * Where a pass-through page that failed sends the person, as when its code could not load. A
 * sign-out page goes to a sign-in page only: the site its address names once the API knows it, else
 * this site's. Any other page goes back to a hop's site only when the API's record of that hop names
 * it, never a site the address alone names; else to a signed-out site's sign-in page; else to this
 * site's sign-in page. Never an error page of this site's own.
 */
export async function passThroughFailureDestination(apiBaseUrl: string, redirectPath: string, state: PassThroughState): Promise<string> {
  if (isRoutePath(hopSignOutPath, state.pathname)) {
    const named = readSignOutReturn(state.hash)
    if (named !== null && (await isKnownSite(apiBaseUrl, named))) {
      return `${named}/login`
    }
    return state.signOutReturn === null ? '/login' : `${state.signOutReturn}/login`
  }
  // A hop page sent with a hop's id serves that hop, which nothing in this tab records yet: a
  // pending hop here is an older one's.
  const isNewHop = isHopPagePath(state.pathname) && readHopId(state.hash) !== null
  if (state.pendingReturn !== null && !isNewHop) {
    return hopFailureAddress(state.pendingReturn, redirectPath, 'other')
  }
  if (state.signOutReturn !== null) {
    return `${state.signOutReturn}/login`
  }
  return '/login'
}

/**
 * Catches a pass-through page's failure, a chunk that could not load included: keeps nothing of the
 * hop in this tab, its pending record and MSAL's cache for it included, and sends the person on (see
 * {@link passThroughFailureDestination}) rather than show an error page of this site's own.
 */
export class PassThroughBoundary extends Component<
  {
    readonly apiBaseUrl: string
    readonly redirectPath: string
    readonly msalInstance: IPublicClientApplication | null
    readonly children: ReactNode
  },
  { readonly hasFailed: boolean }
> {
  override state = { hasFailed: false }

  static getDerivedStateFromError(): { hasFailed: boolean } {
    return { hasFailed: true }
  }

  override componentDidCatch(error: unknown): void {
    log(LogLevel.Error, 'A sign-in pass-through page failed.', error instanceof Error ? error : { error: String(error) })
    void this.leave()
  }

  // Nothing of the hop is kept here before anything waits on the network, so a tab closed meanwhile
  // keeps nothing either; then where to, then away.
  private async leave(): Promise<void> {
    const state = readPassThroughState()
    holdHopSignIn(null)
    abandonPendingHop()
    clearSignOutReturn()
    await forgetHopTabSignIn(this.props.msalInstance)
    hopNavigation.replace(await passThroughFailureDestination(this.props.apiBaseUrl, this.props.redirectPath, state))
  }

  override render(): ReactNode {
    return this.state.hasFailed ? <PassThrough /> : this.props.children
  }
}
